import type { ListBuilderConfiguration, ListBuilderList } from '../types';

/**
 * PD-16797 — the trip builder's account list for a SERVICE trip: the same
 * customers as the delivery list (`dispatch_candidate`, same endpoint and
 * field keys), ranked by service work instead of ice need. Accounts with an
 * open service order due by the trip date come first, then any open service
 * order, then the rest (the server orders them; `service` is its sort key).
 * Columns show the service waiting, sensor trouble and the last service day.
 */
const SENSOR_VALUE_MAPPING = [{ value: true, label: 'Sensor down', color: { type: 'danger' } }];

const BLOCKED_VALUE_MAPPING = [
	{ value: 'credit_hold', label: 'Credit hold', color: { type: 'danger' } },
];

const field = (fieldName: string) => ({
	fieldName,
	objectTable: 'dispatch_candidate',
});

const defaultServiceCandidateList: ListBuilderList = {
	id: 'default-dispatch-service-candidates',
	name: 'Accounts',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {},
	columns: [
		{
			id: 'dispatch-service-candidate-account',
			fields: [
				{
					id: 'dispatch-service-candidate-name',
					dataConfig: { type: 'field', field: field('name'), defaultValue: '-' },
					link: { enabled: true, type: 'object', objectTable: 'client', idField: 'clientId' },
					styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				{
					id: 'dispatch-service-candidate-account-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'dispatch-service-candidate-address',
					dataConfig: { type: 'field', field: field('addressLabel'), defaultValue: 'No address' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Account',
			headers: [{ id: 'h-dispatch-service-candidate-account', text: 'Account' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('name') }],
			minWidth: 240,
			widthConfig: 'large',
		},
		{
			id: 'dispatch-service-candidate-waiting',
			fields: [
				{
					id: 'dispatch-service-candidate-waiting-label',
					dataConfig: {
						type: 'field',
						field: field('serviceWaitingLabel'),
						defaultValue: 'No service order',
					},
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
			],
			label: 'Service waiting',
			headers: [{ id: 'h-dispatch-service-candidate-waiting', text: 'Service waiting' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('service') }],
			// Fixed so "2 service orders · 3 lines · due 10/9" is not cut short
			// when the list sits narrow beside the map.
			width: 230,
		},
		{
			id: 'dispatch-service-candidate-sensors',
			fields: [
				// An empty badge renders "-", so the badge shows only when a
				// sensor is down; otherwise the reading line stands alone.
				{
					id: 'dispatch-service-candidate-sensor-down',
					dataConfig: { type: 'field', field: field('sensorDown') },
					styleConfig: { size: 'sm', valueMapping: SENSOR_VALUE_MAPPING },
					displayStyle: 'badge',
					conditionalVisibility: { field: 'sensorDown', condition: 'equals', value: true },
				},
				{
					id: 'dispatch-service-candidate-units',
					dataConfig: { type: 'field', field: field('needLabel'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Sensors',
			headers: [{ id: 'h-dispatch-service-candidate-sensors', text: 'Sensors' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 150,
			widthConfig: 'medium',
		},
		{
			id: 'dispatch-service-candidate-last-serviced',
			fields: [
				{
					id: 'dispatch-service-candidate-last-serviced-date',
					dataConfig: { type: 'field', field: field('lastServiceDate'), defaultValue: 'Never' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
						formatOptions: { relativeBase: 'today', relativeUnit: 'day' },
					},
					displayStyle: 'relative_date',
				},
			],
			label: 'Last serviced',
			headers: [{ id: 'h-dispatch-service-candidate-last-serviced', text: 'Last serviced' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('lastServiced') }],
			minWidth: 120,
			widthConfig: 'small',
		},
		{
			id: 'dispatch-service-candidate-trip',
			fields: [
				{
					id: 'dispatch-service-candidate-trip-label',
					dataConfig: { type: 'field', field: field('tripLabel'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				{
					id: 'dispatch-service-candidate-trip-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'blockedReason', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-service-candidate-blocked',
					dataConfig: { type: 'field', field: field('blockedReason') },
					styleConfig: { size: 'sm', valueMapping: BLOCKED_VALUE_MAPPING },
					displayStyle: 'badge',
					conditionalVisibility: { field: 'blockedReason', condition: 'is_not_null' },
				},
			],
			label: 'Trip',
			headers: [{ id: 'h-dispatch-service-candidate-trip', text: 'Trip' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 140,
			widthConfig: 'medium',
		},
		{
			id: 'dispatch-service-candidate-route',
			fields: [
				{
					id: 'dispatch-service-candidate-route-names',
					dataConfig: { type: 'field', field: field('routeNames'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Route',
			headers: [{ id: 'h-dispatch-service-candidate-route', text: 'Route' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 120,
			widthConfig: 'small',
		},
	],
	defaultSortId: 'dispatch-service-candidate-service',
	actions: [],
};

export const DISPATCH_SERVICE_CANDIDATE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [defaultServiceCandidateList],
	tableSorts: [
		{
			id: 'dispatch-service-candidate-service',
			name: 'Service due first',
			description: 'Service due by the trip date, then any open service order',
			fields: [{ direction: 'ASC', ...field('service') }],
		},
		{
			id: 'dispatch-service-candidate-last-serviced-asc',
			name: 'Longest since service',
			fields: [{ direction: 'ASC', ...field('lastServiced') }],
		},
		{
			id: 'dispatch-service-candidate-city-asc',
			name: 'City (A-Z)',
			fields: [{ direction: 'ASC', ...field('city') }],
		},
		{
			id: 'dispatch-service-candidate-name-asc',
			name: 'Name (A-Z)',
			fields: [{ direction: 'ASC', ...field('name') }],
		},
	],
	showResetButton: true,
	filters: {
		activeFilters: [
			// PD-16956 — name/address search, as on the delivery builder.
			'dispatchSearch',
			'dispatchServiceDue',
			'dispatchHasServiceOrder',
			'dispatchSensorDown',
			'dispatchNotServicedDays',
			'dispatchRoute',
		],
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Account',
			objectTable: 'dispatch_candidate',
			selectedFields: [
				{ fieldName: 'name', fieldLabel: 'Account' },
				{ fieldName: 'addressLabel', fieldLabel: 'Address' },
				{ fieldName: 'serviceWaitingLabel', fieldLabel: 'Service waiting' },
				{ fieldName: 'needLabel', fieldLabel: 'Sensors' },
				{ fieldName: 'lastServiceDate', fieldLabel: 'Last serviced' },
				{ fieldName: 'tripLabel', fieldLabel: 'Trip' },
				{ fieldName: 'routeNames', fieldLabel: 'Route' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
